import React, { useRef, useState, useCallback, useEffect } from 'react';

/**
 * TacticalCanvasBase -- SVG-based tactical field that supports multiple sports.
 *
 * Props:
 *  sportKey   'football' | 'football_american'
 *  players    [{id, label, x, y, color?}]   -- x/y in 0-100 range
 *  paths      [{fromId, toId, waypoints?, style?}]
 *  onPlayerMove(id, x, y)  -- drag callback
 *  readOnly   boolean
 *  width      number (container px, default 800)
 *  height     number (container px, default derived from aspect)
 */

const FIELD_CONFIG = {
  football: {
    // Soccer pitch 105m x 68m  =>  aspect 1.544
    aspect: 105 / 68,
    lineColor: 'rgba(255,255,255,0.5)',
    fillColor: '#2d6b3f',
    label: 'Campo de Futebol',
    drawMarkings: (ctx, w, h) => {
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 1;
      // Center line
      ctx.beginPath();
      ctx.moveTo(w / 2, 0);
      ctx.lineTo(w / 2, h);
      ctx.stroke();
      // Center circle
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, h * 0.15, 0, Math.PI * 2);
      ctx.stroke();
      // Penalty areas (approx 16.5m / 68m = 0.2426)
      const paW = w * (16.5 / 105);
      const paH = h * (40.32 / 68);
      const paTop = (h - paH) / 2;
      // Left
      ctx.strokeRect(0, paTop, paW, paH);
      // Right
      ctx.strokeRect(w - paW, paTop, paW, paH);
    },
  },
  football_american: {
    // 100 yards x 53.33 yards => aspect 1.875
    aspect: 100 / 53.33,
    lineColor: 'rgba(255,255,255,0.3)',
    fillColor: '#2d5a4f',
    label: 'Campo de Futebol Americano',
    drawMarkings: (ctx, w, h) => {
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1;
      // Yard lines every 10 yards
      for (let i = 0; i <= 100; i += 10) {
        const x = (i / 100) * w;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      // Hash marks (simplified)
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      for (let i = 0; i <= 100; i += 5) {
        const x = (i / 100) * w;
        ctx.beginPath();
        ctx.moveTo(x, h * 0.33);
        ctx.lineTo(x, h * 0.33 + 6);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x, h * 0.66);
        ctx.lineTo(x, h * 0.66 + 6);
        ctx.stroke();
      }
    },
  },
};

const TacticalCanvasBase = ({
  sportKey = 'football',
  players = [],
  paths = [],
  onPlayerMove,
  readOnly = false,
  width = 800,
  height: heightProp,
}) => {
  const config = FIELD_CONFIG[sportKey] || FIELD_CONFIG.football;
  const height = heightProp || Math.round(width / config.aspect);

  const canvasRef = useRef(null);
  const [dragging, setDragging] = useState(null);

  // ---- Drawing ----
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);

    // Field fill
    ctx.fillStyle = config.fillColor;
    ctx.fillRect(0, 0, width, height);

    // Border
    ctx.strokeStyle = config.lineColor;
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    // Sport-specific markings
    config.drawMarkings(ctx, width, height);

    // Paths (arrows)
    paths.forEach((p) => {
      const from = players.find((pl) => pl.id === p.fromId);
      const to = players.find((pl) => pl.id === p.toId);
      if (!from || !to) return;
      const fx = (from.x / 100) * width;
      const fy = (from.y / 100) * height;
      const tx = (to.x / 100) * width;
      const ty = (to.y / 100) * height;

      ctx.beginPath();
      ctx.moveTo(fx, fy);
      if (p.waypoints && p.waypoints.length) {
        p.waypoints.forEach((wp) => {
          ctx.lineTo((wp.x / 100) * width, (wp.y / 100) * height);
        });
      }
      ctx.lineTo(tx, ty);
      ctx.strokeStyle = p.style?.color || '#f59e0b';
      ctx.lineWidth = p.style?.width || 2;
      ctx.setLineDash(p.style?.dash || [6, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Arrow head
      const angle = Math.atan2(ty - fy, tx - fx);
      ctx.beginPath();
      ctx.moveTo(tx, ty);
      ctx.lineTo(tx - 10 * Math.cos(angle - 0.4), ty - 10 * Math.sin(angle - 0.4));
      ctx.lineTo(tx - 10 * Math.cos(angle + 0.4), ty - 10 * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fillStyle = p.style?.color || '#f59e0b';
      ctx.fill();
    });

    // Players
    players.forEach((pl) => {
      const x = (pl.x / 100) * width;
      const y = (pl.y / 100) * height;
      ctx.beginPath();
      ctx.arc(x, y, 14, 0, Math.PI * 2);
      ctx.fillStyle = dragging === pl.id ? '#b48c2c' : (pl.color || '#ffffff');
      ctx.fill();
      ctx.strokeStyle = '#111111';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#111111';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(pl.label, x, y);
    });
  }, [players, paths, dragging, width, height, config]);

  useEffect(() => {
    draw();
  }, [draw]);

  // ---- Drag handlers ----
  const toFieldCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  const handleMouseDown = (e) => {
    if (readOnly) return;
    const { x, y } = toFieldCoords(e);
    const hit = players.find((p) => {
      const dx = p.x - x;
      const dy = p.y - y;
      return Math.sqrt(dx * dx + dy * dy) < 3;
    });
    if (hit) setDragging(hit.id);
  };

  const handleMouseMove = (e) => {
    if (dragging == null || readOnly) return;
    const { x, y } = toFieldCoords(e);
    if (onPlayerMove) onPlayerMove(dragging, Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y)));
  };

  const handleMouseUp = () => setDragging(null);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width: '100%', height: 'auto', borderRadius: 12, cursor: readOnly ? 'default' : 'grab' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    />
  );
};

export default TacticalCanvasBase;
